import React from "react";
import { Spin, Form, Input, Button, Select, Checkbox, Modal, message } from "antd";
import { LoadingOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import moment from "moment";
import {
  _get_raw_food_profile,
  _create_raw_food_business,
  _update_raw_food_profile,
} from "../../common/axios_services";
import { useSelector } from "react-redux";
import AllAppRoutes from "../../common/routes";
import StateLgaSelects from "../../components/common/StateLgaSelects";
import useUserProfile from "../../hooks/useUserProfile";
import AutoCompleteAddress from "../../components/AutoCompleteAddress";
import { suggestedStoreBanners } from "../../common/constant";

const RawFoodBusiness = ({ user }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const { userProfile: fetchedUserProfile, refetchUserProfile } =
    useUserProfile();

  const [spinnerLoading, setSpinnerLoading] = React.useState(false);
  const [bannerUrl, setBannerUrl] = React.useState(null);
  const [isBannerModalOpen, setIsBannerModalOpen] = React.useState(false);
  const [updatingProfile, setUpdatingProfile] = React.useState(false);
  const [showDifferentSupplyAddress, setShowDifferentSupplyAddress] =
    React.useState(false);
  const [contactModalVisible, setContactModalVisible] = React.useState(false);
  const [contactPerson, setContactPerson] = React.useState("");
  const [contactTelephone, setContactTelephone] = React.useState("");
  const [contactForm] = Form.useForm();

  const finishProfile = async (values) => {
    const data = {
      bannerImage: bannerUrl || "",
      supplyAddress: values.showDifferentSupplyAddress
        ? values.supplyAddressString
        : undefined,
      contactPerson: contactPerson,
      contactTelephone: contactTelephone,
    };

    try {
      setUpdatingProfile(true);
      let response;

      try {
        response = await _update_raw_food_profile(data);
      } catch (updateError) {
        if (updateError.response?.status === 404) {
          response = await _create_raw_food_business(data);
        } else {
          throw updateError;
        }
      }

      showSuccessNotification(response?.data?.message);
      refetchUserProfile();
    } catch (err) {
      showErrorNotification(err.response?.data?.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  const fetchRawFoodProfile = async () => {
    try {
      setSpinnerLoading(true);
      const response = await _get_raw_food_profile();
      const data = response.data?.data?.data || response.data?.data || response.data || {};

      setBannerUrl(data.bannerImage || null);

      setContactPerson(data.contactPerson || user?.userName || "");
      setContactTelephone(data.contactTelephone || user?.phoneNumber || "");

      form.setFieldsValue({
        showDifferentSupplyAddress: Boolean(data.supplyAddress),
        supplyAddressString: data.supplyAddress || undefined,
      });
      setShowDifferentSupplyAddress(Boolean(data.supplyAddress));
    } catch (err) {
      if (err.response?.status === 404) {
        setContactPerson(user?.userName || "");
        setContactTelephone(user?.phoneNumber || "");
        form.setFieldsValue({
          showDifferentSupplyAddress: false,
        });
      } else {
        showErrorNotification(err.response?.data?.message ?? err.message);
      }
    } finally {
      setSpinnerLoading(false);
    }
  };

  React.useEffect(() => {
    if (fetchedUserProfile) {
      fetchRawFoodProfile();
    }
  }, [fetchedUserProfile]);

  const generateTimeOptions = () => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const time = moment({ hour: i, minute: j });
        times.push(time.format("h:mm A"));
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();
  const openTimeOptions = timeOptions;

  const { Option } = Select;

  const handleContactModalOpen = () => {
    contactForm.setFieldsValue({
      contact_person: contactPerson,
      contact_telephone: contactTelephone,
    });
    setContactModalVisible(true);
  };

  const handleContactModalClose = () => {
    setContactModalVisible(false);
    contactForm.resetFields();
  };

  const handleContactSave = async (values) => {
    try {
      setUpdatingProfile(true);
      const updateData = {
        contactPerson: values.contact_person,
        contactTelephone: values.contact_telephone,
      };

      const updateRawFoodProfile = await _update_raw_food_profile(updateData);
      showSuccessNotification(updateRawFoodProfile?.data?.message);

      setContactPerson(values.contact_person);
      setContactTelephone(values.contact_telephone);
      handleContactModalClose();
      refetchUserProfile();
    } catch (err) {
      showErrorNotification(err.response?.data?.message ?? err.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <Spin spinning={spinnerLoading} indicator={newAntIcon}>
      <div className="">
        <Form
          form={form}
          layout="vertical"
          onFinish={finishProfile}
          initialValues={{}}
          style={{ padding: "20px" }}
        >
          <div className="mb-5">
            <div
              className="border border-gray-200 rounded-xl overflow-hidden cursor-pointer bg-gray-50"
              onClick={() => setIsBannerModalOpen(true)}
            >
              {bannerUrl ? (
                <img src={bannerUrl} alt="Wholesale business banner" className="w-full h-48 object-cover" />
              ) : (
                <div className="h-48 flex flex-col items-center justify-center text-gray-500">
                  <PlusOutlined className="text-2xl" />
                  <span className="mt-2">Choose Wholesale Banner</span>
                </div>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-2">Choose the banner customers will see on your Wholesale business profile.</p>
          </div>

          <Modal
            centered
            open={isBannerModalOpen}
            title="Choose a Wholesale Banner"
            onCancel={() => setIsBannerModalOpen(false)}
            footer={null}
          >
            <div className="grid grid-cols-2 gap-3">
              {suggestedStoreBanners.map((banner) => (
                <button
                  type="button"
                  key={banner}
                  onClick={() => {
                    setBannerUrl(banner);
                    setIsBannerModalOpen(false);
                    message.success("Wholesale banner selected. Save your profile to apply it.");
                  }}
                  className={`overflow-hidden rounded-lg border-2 ${banner === bannerUrl ? "border-blue-500" : "border-gray-200"}`}
                >
                  <img src={banner} alt="Wholesale banner option" className="w-full h-24 object-cover" />
                </button>
              ))}
            </div>
          </Modal>

          <p className="profile_category_title">Contact Information</p>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                    <p className="font-medium text-gray-900">
                      {contactPerson || "Not set"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      Contact Telephone
                    </p>
                    <p className="font-medium text-gray-900">
                      {contactTelephone || "Not set"}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={handleContactModalOpen}
                className="text-gray-600 hover:text-gray-900"
              >
                Change
              </Button>
            </div>
          </div>

          <p className="profile_category_title">Supply Information</p>

          <Form.Item name="showDifferentSupplyAddress" valuePropName="checked">
            <Checkbox
              checked={showDifferentSupplyAddress}
              onChange={(e) => setShowDifferentSupplyAddress(e.target.checked)}
            >
              Supply address, different from business address
            </Checkbox>
          </Form.Item>

          {showDifferentSupplyAddress && (
            <>
              <StateLgaSelects
                form={form}
                initialPickupState={form.getFieldValue("supplyState")}
                initialPickupLga={form.getFieldValue("supplyLga")}
                stateFieldName="supplyState"
                lgaFieldName="supplyLga"
              />

              <Form.Item
                label="Supply Address"
                name="supplyAddressString"
                rules={[{ required: true, message: "Please enter the supply address" }]}
              >
                <AutoCompleteAddress placeholder="Supply address" style={{ height: "5rem" }} />
              </Form.Item>
            </>
          )}



          {/* <p className="profile_category_title">Business Hours</p>

          <div>
            <Form.Item
              label="Opening time"
              name="open_time"
              rules={[
                {
                  required: true,
                  message: "Please select your opening time!",
                },
              ]}
            >
              <Select
                placeholder="Select opening time"
                size="large"
                style={{ width: "100%" }}
              >
                {openTimeOptions.map((time) => (
                  <Option key={time} value={time}>
                    {time}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div> */}

          <div>
            <Form.Item label="Description" name="description">
              <Input.TextArea
                placeholder="Enter description here..."
                rows={6}
                className="custom-textarea"
              />
            </Form.Item>
          </div>

          {updatingProfile ? (
            <button
              className="restaurant_profile_button bg-gray-300 text-gray-500 cursor-not-allowed border-none px-6 py-3 rounded-lg flex items-center justify-center transition-colors"
              disabled
            >
              Updating profile. Please wait
              <Spin style={{ marginLeft: "10px" }} indicator={newAntIcon} />
            </button>
          ) : (
            <button
              className="restaurant_profile_button bg-gray-900 text-white hover:bg-gray-800 border-none px-6 py-3 rounded-lg transition-colors"
              htmlType="submit"
            >
              Update Profile
            </button>
          )}
        </Form>
      </div>

      <Modal
        title="Update Contact Information"
        open={contactModalVisible}
        onCancel={handleContactModalClose}
        footer={null}
        centered
      >
        <Form form={contactForm} layout="vertical" onFinish={handleContactSave}>
          <Form.Item
            label="Contact Person"
            name="contact_person"
            rules={[
              {
                required: true,
                message: "Please enter contact person name",
              },
            ]}
          >
            <Input placeholder="Enter contact person name" />
          </Form.Item>

          <Form.Item
            label="Contact Telephone"
            name="contact_telephone"
            rules={[
              {
                required: true,
                message: "Please enter contact telephone",
              },
            ]}
          >
            <Input placeholder="Enter contact telephone" />
          </Form.Item>

          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={handleContactModalClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updatingProfile}
              className="bg-gray-900"
            >
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </Spin>
  );
};

export default RawFoodBusiness;
